import Skeleton from "@mui/material/Skeleton";
import TableContainer from "@mui/material/TableContainer";
import MuiTable from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import VuiBox from "components/VuiBox";
import PropTypes from "prop-types";

/*Bearer ${token}`
 * A shimmer skeleton that mimics a data table while content is loading.
 *
 * @param {object}   props
 * @param {number}   props.rows     Number of placeholder rows (default 5)
 * @param {Array}    props.columns  Column definitions — each { name, align?, width? }
 * @param {boolean}  props.dense    Use compact row height (default false)
 */
function SkeletonTable({ rows, columns, dense }) {
  const cellSx = {
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    py: dense ? 1 : 1.5,
    px: 1.5,
  };

  const skeletonSx = {
    bgcolor: "rgba(255,255,255,0.06)",
    borderRadius: "6px",
    "&::after": {
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
    },
  };

  return (
    <VuiBox sx={{ overflowX: { xs: "auto", sm: "visible" }, "& .MuiTable-root": { minWidth: 700 } }}>
      <TableContainer>
        <MuiTable>
          {/* Header */}
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.name}
                  align={col.align || "left"}
                  sx={{
                    ...cellSx,
                    borderBottom: "1px solid rgba(255,255,255,0.12)",
                    width: col.width,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={col.headerWidth || "50%"}
                    height={12}
                    animation="wave"
                    sx={skeletonSx}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Rows */}
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIdx) => (
              <TableRow key={rowIdx}>
                {columns.map((col, colIdx) => (
                  <TableCell key={col.name} align={col.align || "left"} sx={cellSx}>
                    {col.skeleton === "multi" ? (
                      /* Two-line cell (e.g. name + subtitle) */
                      <VuiBox display="flex" flexDirection="column" gap={0.6}>
                        <Skeleton
                          variant="rounded"
                          width={randomWidth(70, 95)}
                          height={14}
                          animation="wave"
                          sx={skeletonSx}
                        />
                        <Skeleton
                          variant="rounded"
                          width={randomWidth(40, 70)}
                          height={10}
                          animation="wave"
                          sx={skeletonSx}
                        />
                      </VuiBox>
                    ) : col.skeleton === "badge" ? (
                      /* Status badge */
                      <Skeleton
                        variant="rounded"
                        width={60}
                        height={22}
                        animation="wave"
                        sx={{ ...skeletonSx, borderRadius: "11px" }}
                      />
                    ) : col.skeleton === "icon" ? (
                      /* Small action icon */
                      <VuiBox display="flex" justifyContent={col.align || "left"} gap={1}>
                        <Skeleton variant="circular" width={26} height={26} animation="wave" sx={skeletonSx} />
                        <Skeleton variant="circular" width={26} height={26} animation="wave" sx={skeletonSx} />
                      </VuiBox>
                    ) : (
                      /* Default single-line text */
                      <Skeleton
                        variant="rounded"
                        width={randomWidth(50, 90)}
                        height={13}
                        animation="wave"
                        sx={skeletonSx}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </VuiBox>
  );
}

/*Bearer ${token}` Return a CSS percentage width with slight variation per row for realism. */
function randomWidth(min, max) {
  const pct = min + Math.floor(Math.random() * (max - min + 1));
  return `${pct}%`;
}

SkeletonTable.defaultProps = {
  rows: 5,
  dense: false,
  columns: [
    { name: "col1", skeleton: "multi" },
    { name: "col2" },
    { name: "col3" },
    { name: "col4", skeleton: "badge", align: "center" },
    { name: "col5", skeleton: "icon", align: "center" },
  ],
};

SkeletonTable.propTypes = {
  rows: PropTypes.number,
  dense: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      align: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      headerWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      skeleton: PropTypes.oneOf(["text", "multi", "badge", "icon"]),
    })
  ),
};

export default SkeletonTable;


